import { useEffect, useState } from "react";
import { Archive, Code2, Download, ExternalLink, File, FileAudio, FileImage, FileSpreadsheet, FileText, FileType2, FileVideo, Presentation, X, Loader2, AlertCircle } from "lucide-react";
import { useChat } from "@/lib/chat";
import { fileUrl, fmtBytes } from "@/lib/upload";
import { analytics } from "@/lib/analytics";

const ICONS = { image: FileImage, video: FileVideo, audio: FileAudio, pdf: FileType2, doc: FileText, sheet: FileSpreadsheet, slides: Presentation, archive: Archive, code: Code2, text: FileText, file: File };
const LABELS = { pdf: "PDF", doc: "Word document", sheet: "Spreadsheet", slides: "Presentation", archive: "Zip archive", code: "Code", text: "Text file", file: "File", image: "Image", video: "Video", audio: "Audio" };

export const FileIcon = ({ kind, size = 20 }) => { const I = ICONS[kind] || File; return <I size={size} className={`tc-file-icon is-${kind}`} />; };

const trackFile = (action, f) => analytics.track(action, { category: "files", target: "file", target_id: f.id, label: f.name, conversation_id: f.conversation_id, metadata: { kind: f.kind, size: f.size } });

export const FileCard = ({ f, compact }) => (
  <div className={`tc-file-card${compact ? " is-compact" : ""}`} data-testid={`file-card-${f.id}`}>
    <span className="tc-file-card__icon"><FileIcon kind={f.kind} size={compact ? 18 : 24} /></span>
    <div className="tc-file-card__info">
      <div className="tc-file-card__name" title={f.name}>{f.name}</div>
      <div className="tc-file-card__meta">{LABELS[f.kind] || "File"} · {fmtBytes(f.size)}</div>
    </div>
    <div className="tc-file-card__actions">
      {(f.kind === "pdf" || f.kind === "text" || f.kind === "image" || f.kind === "video") && (
        <a className="tc-file-card__btn" href={fileUrl(f)} target="_blank" rel="noreferrer" title="Open in new tab" onClick={() => trackFile("file_opened", f)} data-testid={`file-open-${f.id}`}><ExternalLink size={15} /></a>
      )}
      <a className="tc-file-card__btn" href={fileUrl(f, { download: true })} download={f.name} title="Download" onClick={() => trackFile("file_download_clicked", f)} data-testid={`file-download-${f.id}`}><Download size={15} /></a>
    </div>
  </div>
);

export const Attachments = ({ files, compact }) => {
  const { setModal, prefs } = useChat();
  const previews = prefs.inline_previews !== false;
  if (!files?.length) return null;
  const images = files.filter((f) => f.kind === "image");
  const media = files.filter((f) => f.kind === "video" || f.kind === "audio");
  const others = files.filter((f) => !["image", "video", "audio"].includes(f.kind));
  const openLightbox = (f) => { trackFile("file_preview_opened", f); setModal({ type: "lightbox", files: images, index: images.indexOf(f) }); };
  return (
    <div className="tc-attachments" data-testid="message-attachments">
      {previews && images.length > 0 && (
        <div className={`tc-attach-images${images.length > 1 ? " is-grid" : ""}`}>
          {images.map((f) => {
            const ratio = f.width && f.height ? f.width / f.height : 4 / 3;
            const w = images.length > 1 || compact ? 180 : Math.min(360, Math.max(120, Math.round(240 * ratio)));
            return (
              <button key={f.id} className="tc-attach-image" style={{ width: w, aspectRatio: images.length > 1 ? "1 / 1" : String(ratio) }} onClick={() => openLightbox(f)} title={`${f.name} · ${fmtBytes(f.size)}`} data-testid={`image-attachment-${f.id}`}>
                <img src={fileUrl(f)} alt={f.name} loading="lazy" />
              </button>
            );
          })}
        </div>
      )}
      {!previews && images.map((f) => <FileCard key={f.id} f={f} compact={compact} />)}
      {media.map((f) => (previews && f.kind === "video" ? (
        <div key={f.id} className="tc-attach-video" data-testid={`video-attachment-${f.id}`}>
          <video controls preload="metadata" src={fileUrl(f)} onPlay={() => trackFile("file_media_played", f)} />
          <div className="tc-attach-video__meta"><FileIcon kind="video" size={14} /> {f.name} · {fmtBytes(f.size)} <a href={fileUrl(f, { download: true })} download={f.name} title="Download"><Download size={13} /></a></div>
        </div>
      ) : previews && f.kind === "audio" ? (
        <div key={f.id} className="tc-attach-audio" data-testid={`audio-attachment-${f.id}`}>
          <FileIcon kind="audio" size={18} /><span className="tc-attach-audio__name">{f.name}</span><audio controls preload="metadata" src={fileUrl(f)} onPlay={() => trackFile("file_media_played", f)} />
        </div>
      ) : <FileCard key={f.id} f={f} compact={compact} />))}
      {others.map((f) => <FileCard key={f.id} f={f} compact={compact} />)}
    </div>
  );
};

export const AttachmentTray = ({ items, onRemove }) => {
  if (!items.length) return null;
  return (
    <div className="tc-upload-tray" data-testid="upload-tray">
      {items.map((it) => <UploadTile key={it.key} it={it} onRemove={() => onRemove(it.key)} />)}
    </div>
  );
};

const UploadTile = ({ it, onRemove }) => {
  const isImg = it.file.type.startsWith("image/");
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!isImg) return undefined;
    const u = URL.createObjectURL(it.file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [it.file, isImg]);
  const kind = it.result?.kind || (isImg ? "image" : it.file.type.startsWith("video/") ? "video" : it.file.type === "application/pdf" ? "pdf" : "file");
  return (
    <div className={`tc-upload-tile${it.error ? " is-error" : ""}${it.result ? " is-done" : ""}`} title={it.error || `${it.file.name} · ${fmtBytes(it.file.size)}`} data-testid={`upload-tile-${it.key}`}>
      {url ? <img src={url} alt="" className="tc-upload-tile__thumb" /> : <span className="tc-upload-tile__icon"><FileIcon kind={kind} size={22} /></span>}
      <div className="tc-upload-tile__info">
        <div className="tc-upload-tile__name">{it.file.name}</div>
        <div className="tc-upload-tile__meta">{it.error ? <><AlertCircle size={12} /> {it.error}</> : it.result ? fmtBytes(it.file.size) : <><Loader2 size={12} className="tc-spin" /> Uploading… {Math.round(it.progress * 100)}%</>}</div>
        {!it.result && !it.error && <div className="tc-upload-tile__bar"><span style={{ width: `${Math.round(it.progress * 100)}%` }} /></div>}
      </div>
      <button className="tc-upload-tile__remove" onClick={onRemove} title="Remove" data-testid={`upload-remove-${it.key}`}><X size={12} /></button>
    </div>
  );
};

export const Lightbox = ({ files, index: initial, onClose }) => {
  const [index, setIndex] = useState(initial || 0);
  const f = files[index];
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") setIndex((i) => (i + 1) % files.length); if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + files.length) % files.length); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [files.length, onClose]);
  if (!f) return null;
  return (
    <div className="tc-lightbox tc-fade-in" onClick={onClose} data-testid="lightbox">
      <div className="tc-lightbox__top" onClick={(e) => e.stopPropagation()}>
        <FileIcon kind="image" size={18} /><span className="tc-lightbox__name">{f.name}</span><span className="tc-lightbox__meta">{f.width && f.height ? `${f.width}×${f.height} · ` : ""}{fmtBytes(f.size)}{files.length > 1 ? ` · ${index + 1} of ${files.length}` : ""}</span>
        <a className="tc-lightbox__btn" href={fileUrl(f, { download: true })} download={f.name} title="Download" onClick={() => trackFile("file_download_clicked", f)} data-testid="lightbox-download"><Download size={18} /></a>
        <a className="tc-lightbox__btn" href={fileUrl(f)} target="_blank" rel="noreferrer" title="Open in new tab" data-testid="lightbox-open"><ExternalLink size={18} /></a>
        <button className="tc-lightbox__btn" onClick={onClose} title="Close (Esc)" data-testid="lightbox-close"><X size={20} /></button>
      </div>
      <img className="tc-lightbox__img" src={fileUrl(f)} alt={f.name} onClick={(e) => e.stopPropagation()} data-testid="lightbox-image" />
      {files.length > 1 && (
        <div className="tc-lightbox__nav" onClick={(e) => e.stopPropagation()}>
          <button className="tc-btn tc-btn--outline sm" onClick={() => setIndex((i) => (i - 1 + files.length) % files.length)} data-testid="lightbox-prev">Previous</button>
          <button className="tc-btn tc-btn--outline sm" onClick={() => setIndex((i) => (i + 1) % files.length)} data-testid="lightbox-next">Next</button>
        </div>
      )}
    </div>
  );
};
